'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface NewsletterSignupProps {
  onSubscribe: (email: string, frequency: string) => void;
}

interface SubscribeResult {
  subscriptionSaved: boolean;
  emailSent: boolean;
  emailError?: string;
}

export default function NewsletterSignup({ onSubscribe }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitResult, setSubmitResult] = useState<SubscribeResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError('');
    setSubmitResult(null);

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, frequency }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      const result: SubscribeResult = {
        subscriptionSaved: data.subscriptionSaved ?? false,
        emailSent: data.emailSent ?? false,
        emailError: data.emailError,
      };

      setSubmitResult(result);
      onSubscribe(email, frequency);
      setIsSubmitted(true);

      setTimeout(() => {
        setEmail('');
        setIsSubmitted(false);
        setSubmitResult(null);
      }, 7000);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-primary to-secondary rounded-lg shadow-organic p-8 border border-primary/20">
      <div className="text-center space-y-4 mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full">
          <Icon name="EnvelopeIcon" size={32} variant="solid" className="text-primary-foreground" />
        </div>
        <h3 className="text-2xl font-headline font-bold text-primary-foreground">
          Stay Informed
        </h3>
        <p className="text-primary-foreground/90 font-body">
          Get the latest rhino conservation news delivered to your inbox
        </p>
      </div>
      
      {isSubmitted ? (
        <div className="space-y-3">
          {/* Subscription saved */}
          <div className="bg-success/20 border border-success/30 rounded-lg p-4 text-center">
            <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success mx-auto mb-2" />
            <p className="text-primary-foreground font-body font-semibold">
              Successfully subscribed!
            </p>
          </div>

          {/* Email status */}
          {submitResult?.emailSent ? (
            <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-center">
              <p className="text-primary-foreground/90 font-body text-sm">
                ✉️ Confirmation email sent — check your inbox.
              </p>
            </div>
          ) : (
            <div className="bg-yellow-500/20 border border-yellow-400/30 rounded-lg p-3 text-center">
              <p className="text-primary-foreground/90 font-body text-sm">
                ⚠️ {submitResult?.emailError || 'Confirmation email could not be sent right now. Your subscription was saved.'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-body font-medium text-primary-foreground mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={isLoading}
              className="w-full px-4 py-3 bg-white/90 border border-white/20 rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-white font-body disabled:opacity-60"
            />
          </div>
          
          <div>
            <label htmlFor="frequency" className="block text-sm font-body font-medium text-primary-foreground mb-2">
              Update Frequency
            </label>
            <select
              id="frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              disabled={isLoading}
              className="w-full px-4 py-3 bg-white/90 border border-white/20 rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-white font-body disabled:opacity-60"
            >
              <option value="daily">Daily Updates</option>
              <option value="weekly">Weekly Digest</option>
              <option value="monthly">Monthly Summary</option>
              <option value="breaking">Breaking News Only</option>
            </select>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-400/30 rounded-md p-3 flex items-start space-x-2">
              <Icon name="ExclamationCircleIcon" size={18} variant="solid" className="text-red-200 mt-0.5 shrink-0" />
              <p className="text-primary-foreground/90 font-body text-sm">{error}</p>
            </div>
          )}
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-6 py-3 bg-white text-primary font-cta text-sm font-semibold rounded-md hover:bg-white/90 shadow-organic-sm transition-organic duration-300 flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                <span>Subscribing...</span>
              </>
            ) : (
              <>
                <span>Subscribe Now</span>
                <Icon name="PaperAirplaneIcon" size={18} variant="solid" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}